import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useSelector } from 'react-redux'
import { faEdit } from '@fortawesome/pro-solid-svg-icons'
import { Modal, Tag } from 'antd'

import {
  getMandatory,
  getName,
  InternshipRequestMetadata,
  Tags
} from '../../../utils/entities/InternshipRequestMetadata'
import DataTable from '../../../Components/antd/Tables/DataTable/DataTable'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { Action, DeleteAction } from '../../../Components/antd/Tables/DataTable/utils/actions'
import { HttpStatuses } from '../../../utils/http'
import { onError, onSuccess } from '../../../utils/apiHelper'
import {
  getAllInternshipRequestMetadata,
  createInternshipRequestMetadata,
  updateInternshipRequestMetadata,
  deleteInternshipRequestMetadata
} from '../../../utils/api/internshipRequestMetadata'
import { selectUser } from '../../../reducers/UserReducer'
import InternshipRequestMetadataForm from './InternshipRequestMetadataForm'
import { TableParameters } from '../../../utils/entities/tableParameters'

const InternshipRequestMetadataTable = () => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState(new TableParameters())
  const [data, setData] = useState([])
  const [createdData, setCreatedData] = useState(null)
  const [editedData, setEditedData] = useState(null)

  const fetchAllMetadata = useCallback(() => {
    if (user) {
      setLoading(true)

      getAllInternshipRequestMetadata(user, filters).then(json => {
        if (json?.data) {
          setData(json?.data)
        }

        setLoading(false)
      })
    }
  }, [user, filters])

  const createMetadata = useCallback(data => {
    setCreatedData(null)

    createInternshipRequestMetadata(
      new InternshipRequestMetadata({ ...data, institution: user.institutions[0] }),
      user
    ).then(json => {
      onSuccess(t('metadata_form.creation.success'))
      fetchAllMetadata()
    }).catch(response => {
      displayErrorMessage('create', response.status)
    })
  }, [user, t, fetchAllMetadata, createInternshipRequestMetadata])

  const updateMetadata = useCallback(data => {
    setEditedData(null)

    updateInternshipRequestMetadata(
      new InternshipRequestMetadata({ ...data }),
      user
    ).then(json => {
      onSuccess(t('metadata_form.update.success'))
      fetchAllMetadata()
    }).catch(response => {
      displayErrorMessage('update', response.status)
    })
  }, [user, t, fetchAllMetadata, updateInternshipRequestMetadata])

  const displayErrorMessage = useCallback((type, status) => {
    let message = 'metadata_form.' + type + '.error' + '. Error code: ' + status

    if (status === HttpStatuses.CONFLICT) {
      message = 'metadata_form.name.conflict'
    }

    onError(t(message))
  }, [t])

  const deleteMetadata = useCallback(data => {
    deleteInternshipRequestMetadata(data, user).then(json => {
      onSuccess(t('metadata_form.delete.success'))
      fetchAllMetadata()
    }).catch(response => {
      onError(t('metadata_form.delete.error') + '. Error code: ' + response.status)
    })
  }, [user, t, onSuccess, onError, fetchAllMetadata, deleteInternshipRequestMetadata])

  const columns = useMemo(() => [
    { title: t('common.name'), key: getName },
    {
      title: t('common.mandatory'),
      key: m => <Tag color={Tags[getMandatory(m)].color}> {t(Tags[getMandatory(m)].label)} </Tag>
    }
  ], [t])

  const actions = useMemo(() => [
    new Action({
      title: 'Edit',
      icon: faEdit,
      onClick: setEditedData
    }),
    new DeleteAction({
      onDelete: deleteMetadata
    })
  ], [faEdit, setEditedData, deleteMetadata])

  const timer = useRef(null)

  useEffect(() => {
    clearTimeout(timer.current)
    timer.current = setTimeout(() => fetchAllMetadata(), 300)
  }, [fetchAllMetadata, timer])

  return (
    <>
      <DataTable
        actions={actions}
        columns={columns}
        data={data}
        parameters={filters}
        footerStyle={{ paddingRight: '110px' }}
        loading={loading}
        onCreate={() => setCreatedData({ name: null })}
        onParametersChange={setFilters}
      />
      <Modal
        width={600}
        title={<h3 style={{ textAlign: 'center', margin: '0px' }}> {t('metadata_form.add_field')} </h3>}
        visible={!!createdData}
        footer={false}
        onCancel={() => setCreatedData(null)}
        destroyOnClose
      >
        <InternshipRequestMetadataForm
          data={createdData}
          onSubmit={createMetadata}
        />
      </Modal>
      <Modal
        width={600}
        title={<h3 style={{ textAlign: 'center', margin: '0px' }}> {t('metadata_form.update_field')} </h3>}
        visible={!!editedData}
        footer={false}
        onCancel={() => setEditedData(null)}
        destroyOnClose
      >
        <InternshipRequestMetadataForm
          data={editedData}
          onSubmit={updateMetadata}
        />
      </Modal>
    </>
  )
}

export default InternshipRequestMetadataTable
